import { useEffect, useState } from 'react';

import styled from 'styled-components';

import { Text } from '@components/text';
import { useIsOverflowing } from '@hooks/use-is-overflowing';
import { useScopedTranslate } from '@locale/translate';
import { lineClamp } from '@styles/layout-helpers';
import { theme } from '@styles/theme';

type ExpandableTextProps = {
  text?: string | null;
  maxLines?: number;
};

export const ExpandableText = ({ text, maxLines = 3 }: ExpandableTextProps) => {
  const { t } = useScopedTranslate(locale);
  const [isExpanded, setIsExpanded] = useState(false);
  const [textRef, isCurrentlyOverflowing] = useIsOverflowing('vertical');
  const [canExpand, setCanExpand] = useState(false);

  useEffect(() => {
    if (!isExpanded && isCurrentlyOverflowing) {
      setCanExpand(true);
    }
  }, [isExpanded, isCurrentlyOverflowing]);

  if (!text) return null;

  const showToggle = canExpand || isCurrentlyOverflowing;

  return (
    <div>
      <StyledText
        ref={textRef}
        font="regular14"
        color="textSecondary"
        text={text}
        $isExpanded={isExpanded}
        $maxLines={maxLines}
      />
      {showToggle && (
        <ToggleButton type="button" onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? t('showLess') : t('showMore')}
        </ToggleButton>
      )}
    </div>
  );
};

const StyledText = styled(Text)<{ $isExpanded: boolean; $maxLines: number }>(
  ({ $isExpanded, $maxLines }) => ({
    ...(!$isExpanded && lineClamp('-webkit-box', $maxLines)),
  })
);

const ToggleButton = styled.button({
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  outline: 'none',
  color: theme.color.textSecondary,
  font: theme.font.semibold14,
  cursor: 'pointer',
  textAlign: 'left',
  ':hover': {
    color: theme.color.blue100,
    textDecoration: 'underline',
  },
  ':focus': {
    outline: 'none',
  },
});

const locale = {
  en: {
    showMore: 'Show more...',
    showLess: 'Show less...',
  },
  is: {
    showMore: 'Sjá meira...',
    showLess: 'Sjá minna...',
  },
};
